<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { VBadge, VButton, VIcon } from '@/modules/shared/components'
import VSwitch from '@/modules/shared/components/VSwitch.vue'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useTaskListStore } from '../stores/tasking-store'
import Task from './Task.vue'
import TaskListSwitch from './TaskListSwitch.vue'

const props = defineProps<{
  taskListId: number | null
  slug: string
}>()

const store = useTaskListStore()
const authStore = useAuthStore()

const isOnHold = () => store.taskList?.on_hold ?? false

const toggleOnHold = async () => {
  await store.toggleOnHold(props.taskListId)
}

// Per-task expand
const expandedIndex = ref<number | null>(null)

const isExpanded = (index: number) => expandedIndex.value === index

const toggleExpand = (index: number) => {
  expandedIndex.value = expandedIndex.value === index ? null : index
}

// Featured task (first task, e.g. "Complete Payment")
// All tasks displayed in the grouped card
const groupedTasks = computed(() => store.taskList?.tasks ?? [])

const isAllComplete = computed(
  () =>
    !!store.taskList &&
    store.taskList.tasks_count > 0 &&
    store.taskList.tasks_complete_count === store.taskList.tasks_count,
)

// Set the expanded index
watch(
  () => store.taskList,
  (newTaskList) => {
    // If the current expanded task is incomplete, do not change the expanded index
    if (expandedIndex.value !== null) {
      const task = newTaskList?.tasks[expandedIndex.value]
      if (task && store.isTaskIncomplete(task)) {
        return
      }
    }

    if (newTaskList?.tasks?.length > 0) {
      const firstIncompleteTask = newTaskList.tasks.find((task) => !store.isTaskComplete(task))
      if (firstIncompleteTask) {
        expandedIndex.value = newTaskList.tasks.indexOf(firstIncompleteTask)
      }
    }
  },
  { immediate: true, deep: true },
)
onBeforeRouteLeave((to, from, next) => {
  store.reset()
  next()
})
onMounted(async () => {
  store.setSlug(props.slug)
  await store.fetchTaskList(props.taskListId)
})
</script>

<template>
  <div v-if="store.taskList" class="animate-fade-in">
    <transition name="fold">
      <div>
        <!-- Grouped tasks card -->
        <div v-if="store.taskList.is_visible && groupedTasks.length">
          <!-- Card header (clickable to collapse/expand) -->
          <div class="flex items-center justify-between border-b border-gray-100 pb-3">
            <div class="flex items-center gap-3">
              <div>
                <h3 class="text-sm font-semibold text-gray-900">Venture360 Services</h3>
                <VBadge color="v-green">
                  <span>{{ store.taskList?.tasks_complete_count }} / {{ store.taskList?.tasks_count }} Completed</span>
                </VBadge>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <div
                v-if="authStore.is_site_admin"
                class="flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1"
                @click.stop
              >
                <span class="text-xs text-gray-500">On Hold</span>
                <VSwitch :enabled="isOnHold()" @click="toggleOnHold" />
              </div>
              <div @click.stop>
                <VButton :click="() => store.completeTaskList(taskListId)">Complete</VButton>
              </div>
            </div>
          </div>

          <!-- Task rows (collapsible) -->
          <transition name="card-fold">
            <div>
              <Task
                v-for="(task, index) in groupedTasks"
                :key="task.id"
                :isExpanded="isExpanded(index + 1)"
                :isLoading="store.isLoading(task.id)"
                :task="task"
                @click.prevent="toggleExpand(index + 1)"
              />
            </div>
          </transition>
        </div>

        <!-- Collapsed card (completed or hidden) -->
        <div
          v-else-if="!store.taskList.is_visible"
          class="flex items-center justify-between border-b border-gray-100 pb-3"
        >
          <div>
            <h3 class="text-sm font-semibold text-gray-900">Venture360 Services</h3>
            <VBadge v-if="isAllComplete" color="v-green">
              <span
                >Tasks completed ({{ store.taskList.tasks_complete_count }} / {{ store.taskList.tasks_count }})</span
              >
            </VBadge>
            <span v-else class="text-xs text-gray-500">Tasks hidden</span>
          </div>
          <div class="flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1" @click.stop>
            <span class="text-xs text-gray-500">Show</span>
            <TaskListSwitch :taskListId="store.taskList.id" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.fold-enter-active,
.fold-leave-active {
  transition:
    max-height 0.4s ease,
    opacity 0.3s ease;
}
.fold-enter-from,
.fold-leave-to {
  max-height: 0;
  opacity: 0;
}
.fold-enter-to,
.fold-leave-from {
  max-height: 2000px;
  opacity: 1;
}

.card-fold-enter-active,
.card-fold-leave-active {
  transition:
    max-height 0.3s ease,
    opacity 0.2s ease;
  overflow: hidden;
}
.card-fold-enter-from,
.card-fold-leave-to {
  max-height: 0;
  opacity: 0;
}
.card-fold-enter-to,
.card-fold-leave-from {
  max-height: 2000px;
  opacity: 1;
}
</style>
